'use client';

import { useState, useEffect, useMemo, useCallback } from 'react';
import type { FormValues, FormErrors, FormTouched, FormInstance, StepConfig } from '../types';

interface UseAddMoreFormProps {
  stepId: string;
  step: StepConfig;
  values: FormValues;
  completedForms: Record<string, FormInstance[]>;
  setCompletedForms: React.Dispatch<React.SetStateAction<Record<string, FormInstance[]>>>;
  setValues: React.Dispatch<React.SetStateAction<FormValues>>;
  setErrors: React.Dispatch<React.SetStateAction<FormErrors>>;
  setTouched: React.Dispatch<React.SetStateAction<FormTouched>>;
  groupKey: string; // e.g., 'children-group' or 'guardian-group'
  instancePrefix: string; // e.g., 'card-children-' or 'card-guardian-'
  fieldIds: readonly string[]; // Array of field IDs to collect
  maxInstances?: number; // From card/addMoreConfig; fallback 10 if undefined
  autoShowCondition?: (values: FormValues) => boolean; // Optional condition to auto-show form
}

export interface UseAddMoreFormReturn {
  isAddMoreStep: boolean;
  instances: FormInstance[];
  isFormVisible: boolean;
  showForm: boolean;
  setShowForm: React.Dispatch<React.SetStateAction<boolean>>;
  hasFormValues: boolean;
  handleAdd: () => void;
  getFormData: () => FormValues;
  clearFormFields: (currentValues: FormValues) => FormValues;
  editingInstanceId: string | null;
  setEditingInstanceId: React.Dispatch<React.SetStateAction<string | null>>;
  originalInstanceData: FormInstance | null;
  setOriginalInstanceData: React.Dispatch<React.SetStateAction<FormInstance | null>>;
}

/**
 * Generic hook for managing "Add More" form logic
 * Can be used for children, guardian, or any other multi-instance form
 */
export const useAddMoreForm = ({
  stepId,
  step,
  values,
  completedForms,
  setCompletedForms,
  setValues,
  setErrors,
  setTouched,
  groupKey,
  instancePrefix,
  fieldIds,
  maxInstances,
  autoShowCondition,
}: UseAddMoreFormProps): UseAddMoreFormReturn => {
  const [showForm, setShowForm] = useState(false);
  const [editingInstanceId, setEditingInstanceId] = useState<string | null>(null);
  const [originalInstanceData, setOriginalInstanceData] = useState<FormInstance | null>(null);

  const isAddMoreStep = step.addMore === true && step.id === stepId;

  const instances = useMemo(() => completedForms[groupKey] || [], [completedForms, groupKey]);

  const hasFormValues = useMemo(
    () => fieldIds.some((fieldId) => values[fieldId] !== undefined && values[fieldId] !== ''),
    [values, fieldIds]
  );

  const isFormVisible = showForm || hasFormValues;

  // Restore original instance if form is closed without saving
  useEffect(() => {
    // If editing was cancelled (form closed, editing state still set, but instance might be missing)
    if (!showForm && editingInstanceId && originalInstanceData) {
      setCompletedForms((prev) => {
        const formInstances = prev[groupKey] || [];
        const instanceExists = formInstances.some((inst) => inst.instanceId === editingInstanceId);

        if (!instanceExists) {
          // Restore the original instance (user cancelled edit)
          return {
            ...prev,
            [groupKey]: [...formInstances, originalInstanceData],
          };
        }
        return prev;
      });

      // Clear editing state
      setEditingInstanceId(null);
      setOriginalInstanceData(null);
    }
  }, [showForm, editingInstanceId, originalInstanceData, groupKey, setCompletedForms]);

  // Auto-show form based on condition (e.g., hasChildren === 'yes')
  // If no autoShowCondition is provided, show form initially if no instances exist
  useEffect(() => {
    // Don't auto-show form if instances exist (user should use inline card edit)
    // BUT: Allow manual showing via setShowForm(true) - don't hide if user explicitly shows it
    if (instances.length > 0) {
      // If stale addMore values are present while instances already exist,
      // hide the form and clear those transient fields.
      if (showForm && !editingInstanceId && hasFormValues) {
        setValues((prev) => {
          const nextValues = { ...prev };
          fieldIds.forEach((fieldId) => {
            delete nextValues[fieldId];
          });
          return nextValues;
        });
        setShowForm(false);
      }
      // Don't return early - allow other logic to run for manual show
      if (autoShowCondition) {
        const shouldShow = autoShowCondition(values);
        // Don't hide form when user is editing an inline card - preserve inline edit form
        if (!shouldShow && !editingInstanceId) {
          setShowForm(false);
        }
      }
      return;
    }

    // If user is on the addMore step and has form values (from section card edit),
    // show the form automatically ONLY if no instances exist
    if (isAddMoreStep && hasFormValues && !showForm && instances.length === 0) {
      setShowForm(true);
    }

    if (autoShowCondition) {
      const shouldShow = autoShowCondition(values);
      if (shouldShow) {
        // Show form automatically only if no instances have been added yet (first time)
        if (instances.length === 0 && !hasFormValues && !showForm) {
          setShowForm(true);
        }
      } else {
        // Don't hide form when user is editing an inline card - preserve inline edit form
        if (!editingInstanceId) {
          setShowForm(false);
        }
      }
    } else {
      // No autoShowCondition: show form initially if no instances exist (for guardian, etc.)
      if (
        instances.length === 0 &&
        !hasFormValues &&
        !showForm &&
        isAddMoreStep &&
        stepId !== 'step-4-6-guardian' &&
        stepId !== 'step-5-5-guardian'
      ) {
        setShowForm(true);
      }
    }
  }, [
    autoShowCondition,
    values,
    setValues,
    fieldIds,
    instances.length,
    hasFormValues,
    showForm,
    isAddMoreStep,
    stepId,
    editingInstanceId,
  ]);

  // Get form data from current form values
  const getFormData = useCallback((): FormValues => {
    const formData: FormValues = {};
    fieldIds.forEach((fieldId) => {
      if (values[fieldId] !== undefined) {
        formData[fieldId] = values[fieldId];
      }
    });
    return formData;
  }, [values, fieldIds]);

  // Clear form fields from form values
  const clearFormFields = useCallback(
    (currentValues: FormValues): FormValues => {
      const clearedValues = { ...currentValues };
      fieldIds.forEach((fieldId) => {
        delete clearedValues[fieldId];
      });
      return clearedValues;
    },
    [fieldIds]
  );

  // Handle adding an instance
  const handleAdd = useCallback(() => {
    const formData = getFormData();

    setCompletedForms((prev) => {
      const formInstances = prev[groupKey] || [];

      // Enforce max instances from card/config; fallback to 10 if not set
      const limit = maxInstances ?? 5;
      if (formInstances.length >= limit && !editingInstanceId) {
        console.warn(`Maximum instances (${limit}) reached for ${groupKey}`);
        return prev; // Don't add more
      }

      if (editingInstanceId && originalInstanceData) {
        // Update existing instance in place
        const instanceIndex = formInstances.findIndex(
          (inst) => inst.instanceId === editingInstanceId
        );

        if (instanceIndex >= 0) {
          // Update existing instance
          const updatedInstances = [...formInstances];
          updatedInstances[instanceIndex] = {
            ...updatedInstances[instanceIndex],
            data: formData,
          };
          return {
            ...prev,
            [groupKey]: updatedInstances,
          };
        } else {
          // Instance was removed somehow, add it back with updated data
          return {
            ...prev,
            [groupKey]: [
              ...formInstances,
              {
                ...originalInstanceData,
                data: formData,
              },
            ],
          };
        }
      } else {
        // Create new instance
        const instanceId = `${instancePrefix}${Date.now()}`;
        return {
          ...prev,
          [groupKey]: [
            ...formInstances,
            {
              instanceId,
              data: formData,
              lastStepIndex: 0,
            },
          ],
        };
      }
    });

    // Clear only form fields
    const clearedValues = clearFormFields(values);
    setValues(clearedValues);
    setErrors({});
    setTouched({});
    setShowForm(false);
    // Clear editing state after saving
    setEditingInstanceId(null);
    setOriginalInstanceData(null);
  }, [
    getFormData,
    clearFormFields,
    values,
    setCompletedForms,
    setValues,
    setErrors,
    setTouched,
    groupKey,
    instancePrefix,
    maxInstances,
    editingInstanceId,
    originalInstanceData,
  ]);

  return {
    isAddMoreStep,
    instances,
    isFormVisible,
    showForm,
    setShowForm,
    hasFormValues,
    handleAdd,
    getFormData,
    clearFormFields,
    editingInstanceId,
    setEditingInstanceId,
    originalInstanceData,
    setOriginalInstanceData,
  };
};
